import { useMemo, useState } from 'react';
import FileEvidence from './FileEvidence';
import styles from './ImageEvidence.module.css';

function readImages(value) {
  if (value === null || value === undefined || value === '') {
    return { items: [], error: null };
  }

  try {
    let parsed = value;

    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value);
      } catch {
        // Compatibility with the existing single-quoted image metadata.
        parsed = JSON.parse(value.replace(/'/g, '"'));
      }
    }

    if (!Array.isArray(parsed)) {
      throw new Error('Image metadata must contain a list.');
    }

    const valid = parsed.map(image => {
      if (typeof image?.path !== 'string') return null;

      const fileName = image.path.trim().split(/[\\/]/).pop();

      if (!fileName) return null;

      return {
        ...image,
        fileName,
      };
    }).filter(Boolean);

    return {
      items: [...new Map(
        valid.map(image => [image.path, image])
      ).values()],
      error: valid.length === parsed.length
        ? null
        : 'Some image entries have no valid filename.',
    };
  } catch {
    return {
      items: [],
      error: 'The attached image list could not be read.',
    };
  }
}

function ImageGallery({
  companyId,
  siteId,
  assetId,
  images,
  title = 'Images',
}) {
  const [selectedPath, setSelectedPath] = useState(null);

  const { items, error } = useMemo(
    () => readImages(images),
    [images]
  );

  const selectedImage = items.find(
    image => image.path === selectedPath
  );

  return (
    <section className={styles.imagePanel} aria-label={title}>
      <h3 className={styles.imageHeading}>{title}</h3>

      {error && (
        <p className={styles.error} role="alert">{error}</p>
      )}

      {items.length === 0 ? (
        !error && (
          <p className={styles.message}>
            No images are attached.
          </p>
        )
      ) : (
        <div className={styles.imageBody}>
          <div className={styles.imageList}>
            {items.map(image => (
              <button
                key={image.path}
                type="button"
                className={styles.imageItem}
                aria-pressed={selectedPath === image.path}
                onClick={() => setSelectedPath(image.path)}
              >
                {image.name || image.fileName}
              </button>
            ))}
          </div>

          <div className={styles.imagePreview}>
            {selectedImage ? (
              <FileEvidence
                companyId={companyId}
                siteId={siteId}
                assetId={assetId}
                folder="photos"
                fileName={selectedImage.fileName}
                kind="image"
              />
            ) : (
              <p className={styles.message}>
                Select an image to preview it.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default function ImageEvidence(props) {
  const identity = JSON.stringify([
    props.companyId,
    props.siteId,
    props.assetId,
    props.recordId,
  ]);

  return <ImageGallery key={identity} {...props} />;
}